import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import {
  bigUnitNameOf,
  fail,
  handle,
  idOf,
  materialOf,
  nextDocNo,
  parse,
  requireRole,
  stockMovement,
  txLog
} from './c07.shared'
import { notify, wmsRoute } from './notification.shared'
import { binsOfRm } from './rackScope'

/**
 * Endpoint custom C15 Pindah Material (TAT-03 / TAT-04) — `/api/v1/wms/setup-tat/*`. Pendataan Awal (TAT-01 / TAT-02)
 * pindah ke `pendataanAwal.handler.ts` (v7.50: impor Excel + opname pertama).
 * - TAT-03 (desktop): `queue`, `issue-move`, `progress`.
 * - TAT-04 (mobile): `move-gate`, `move-scan-unit`, `move-commit`, `self-contents`, `self-commit`, `suggest-bin`.
 * v7.50 (U-20): setiap kemasan RM berlabel — tidak ada lagi cetak label saat diangkat, sumber "stok awal" tanpa label,
 * atau metrik "Konversi label QR". Antrean = unit berlabel yang raknya melanggar aturan kategori
 * (`mt_rack.category_restriction_id` ≠ kategori material). Tidak ada pembekuan gudang.
 */

const ROLE = {
  admin: 'admin_wh_rm',
  operator: 'operator_wh_rm',
  supervisor: 'wh_rm_supervisor'
} as const
const iso = () => new Date().toISOString()
const up = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}
const num = (value: unknown) => Number(value ?? 0)

/* ============================================================ master & kepatuhan rak */

const sortedBins = () => binsOfRm().sort((a, b) => String(a.code).localeCompare(String(b.code)))
const binOf = (id: unknown) => findRow('mt_rack_bin', idOf(id))
const rackOfBin = (bin: LocalRow | undefined) => (bin ? findRow('mt_rack', idOf(bin.rack_id)) : undefined)
const binByCode = (code: string) => readTable('mt_rack_bin').find(bin => up(bin.code) === up(code))
const zoneByCode = (code: string) => readTable('warehouse_zone').find(zone => up(zone.code) === up(code))
const categoryName = (materialId: unknown) =>
  String(findRow('mt_material_group', idOf(materialOf(materialId)?.category_id))?.name ?? '')
const perBigOf = (materialId: unknown) => Math.max(1, num(materialOf(materialId)?.default_purchase_unit_qty ?? 1))

/** Rak sesuai aturan kategori; tanpa bin (di luar rak) tidak pernah patuh. */
export function isCompliant(binId: unknown, materialId: unknown) {
  const bin = binOf(binId)
  if (!bin) return false
  const rack = rackOfBin(bin)
  if (!rack) return false
  if (!rack.category_restriction_id) return true
  return idOf(materialOf(materialId)?.category_id) === idOf(rack.category_restriction_id)
}

const unitsInStock = () => readTable('rm_unit').filter(unit => unit.unit_status === 'IN_STOCK')
/** Jumlah kemasan (satuan besar) satu unit. */
const unitPacks = (unit: LocalRow) =>
  num(unit.qty_small) / Math.max(1, num(unit.small_per_big_snapshot) || perBigOf(unit.material_id))

/** Material yang berdiri di satu rak: unit berlabel IN_STOCK. */
function occupantsOf(binId: string) {
  const ids = unitsInStock()
    .filter(unit => idOf(unit.rack_bin_id) === binId)
    .map(unit => idOf(unit.material_id))
  return [...new Set(ids.filter(Boolean))]
}

const openTasks = () =>
  readTable('rm_move_task').filter(task => !['COMPLETED', 'CANCELLED'].includes(String(task.task_status)))
const pendingItems = () => {
  const tasks = new Map(openTasks().map(task => [String(task.id), task]))
  return readTable('rm_move_task_item').filter(item => item.item_status === 'PENDING' && tasks.has(idOf(item.task_id)))
}
/** Rak tujuan yang sudah dicadangkan tugas pindah terbuka — tidak diusulkan dua kali. */
const reservedBins = () =>
  new Set(
    pendingItems()
      .map(item => idOf(item.suggested_rack_bin_id))
      .filter(Boolean)
  )

/**
 * Usulan rak tujuan: rak yang sudah berisi material yang sama lebih dulu (menyatukan barang), lalu rak khusus kategorinya
 * yang kosong, lalu rak umum yang kosong. `taken` menampung rak yang sudah diusulkan pada penerbitan yang sama.
 */
export function suggestBin(materialId: string, taken: Set<string> = new Set()) {
  const material = materialOf(materialId)
  if (!material) return null
  const same: LocalRow[] = []
  const emptyRestricted: LocalRow[] = []
  const emptyGeneral: LocalRow[] = []
  sortedBins().forEach(bin => {
    if (bin.is_active === false || taken.has(String(bin.id))) return
    const rack = rackOfBin(bin)
    if (!rack || rack.is_active === false) return
    if (rack.category_restriction_id && idOf(rack.category_restriction_id) !== idOf(material.category_id)) return
    const occupants = occupantsOf(String(bin.id))
    if (occupants.includes(materialId)) return void same.push(bin)
    if (occupants.length) return
    ;(rack.category_restriction_id ? emptyRestricted : emptyGeneral).push(bin)
  })
  return [...same, ...emptyRestricted, ...emptyGeneral][0] ?? null
}

const whyText = (binId: unknown, materialId: unknown) => {
  const bin = binOf(binId)
  if (!bin) return 'Tidak berada di rak mana pun — tumpukan ini perlu dipindahkan ke rak.'
  const rack = rackOfBin(bin)
  const restriction = rack?.category_restriction_id
    ? String(findRow('mt_material_group', idOf(rack.category_restriction_id))?.name ?? '')
    : ''
  return restriction
    ? `Rak khusus ${restriction}, material ini kategori ${categoryName(materialId)}.`
    : `Rak ini tidak diperuntukkan kategori ${categoryName(materialId)}.`
}

/* ============================================================ kemajuan & antrean */

/** Kepatuhan rak: persen kemasan berlabel (IN_STOCK di posisi rak RM) yang berdiri di rak sesuai kategorinya. */
export function progress() {
  const racked = new Set(binsOfRm().map(bin => String(bin.id)))
  const units = unitsInStock().filter(unit => racked.has(idOf(unit.rack_bin_id)))
  const total = units.reduce((sum, unit) => sum + unitPacks(unit), 0)
  const compliant = units
    .filter(unit => isCompliant(unit.rack_bin_id, unit.material_id))
    .reduce((sum, unit) => sum + unitPacks(unit), 0)
  return {
    total: Math.round(total),
    compliant: Math.round(compliant),
    compliant_pct: total ? Math.round((compliant / total) * 100) : 0
  }
}

type QueueRow = {
  key: string
  unit_id: string
  rack_bin_code: string
  rack_name: string | null
  material_id: string
  material_code: string
  material_name: string
  qty: number
  unit_name: string
  qr_code: string
  why: string
  task_no: string | null
  suggested_code: string | null
  suggested_note: string | null
}

const suggestionNote = (bin: LocalRow, materialId: string) => {
  if (occupantsOf(String(bin.id)).includes(materialId))
    return `sudah berisi ${String(materialOf(materialId)?.code)} — disatukan`
  return rackOfBin(bin)?.category_restriction_id ? 'rak khusus kategorinya, masih kosong' : 'rak umum, masih kosong'
}

/** Antrean "Belum Patuh": unit berlabel di posisi rak yang melanggar aturan kategori rak. */
export function queue(): { progress: ReturnType<typeof progress>; rows: QueueRow[]; unplaceable: number } {
  const busyBy = new Map<string, string>()
  pendingItems().forEach(item => {
    const task = findRow('rm_move_task', idOf(item.task_id))
    busyBy.set(idOf(item.unit_id), String(task?.pdh_no ?? ''))
  })
  const taken = reservedBins()
  const racked = new Set(binsOfRm().map(bin => String(bin.id)))
  const rows: QueueRow[] = []
  unitsInStock()
    .filter(unit => racked.has(idOf(unit.rack_bin_id)) && !isCompliant(unit.rack_bin_id, unit.material_id))
    .forEach(unit => {
      const bin = binOf(unit.rack_bin_id) as LocalRow
      const material = materialOf(unit.material_id) as LocalRow
      const taskNo = busyBy.get(String(unit.id)) ?? null
      const target = taskNo ? null : suggestBin(String(unit.material_id), taken)
      if (target) taken.add(String(target.id))
      rows.push({
        key: String(unit.id),
        unit_id: String(unit.id),
        rack_bin_code: String(bin.code),
        rack_name: String(rackOfBin(bin)?.name ?? '') || null,
        material_id: String(unit.material_id),
        material_code: String(material.code),
        material_name: String(material.name),
        qty: Math.round(unitPacks(unit) * 100) / 100,
        unit_name: String(unit.unit_big_snapshot ?? '') || bigUnitNameOf(material),
        qr_code: String(unit.qr_code),
        why: whyText(bin.id, unit.material_id),
        task_no: taskNo,
        suggested_code: target ? String(target.code) : null,
        suggested_note: target ? suggestionNote(target, String(unit.material_id)) : null
      })
    })
  rows.sort(
    (a, b) =>
      Number(Boolean(a.task_no)) - Number(Boolean(b.task_no)) ||
      a.rack_bin_code.localeCompare(b.rack_bin_code) ||
      a.qr_code.localeCompare(b.qr_code)
  )
  return { progress: progress(), rows, unplaceable: rows.filter(row => !row.task_no && !row.suggested_code).length }
}

const codeKind = (raw: string) => {
  const code = up(raw)
  if (binByCode(code)) return 'BIN' as const
  if (zoneByCode(code)) return 'ZONE' as const
  if (readTable('rm_unit').some(unit => up(unit.qr_code) === code)) return 'UNIT' as const
  if (readTable('fg_label').some(label => up(label.label_no) === code)) return 'FG' as const
  return 'UNKNOWN' as const
}

const isOperatorRm = (userId: string) =>
  readTable('mt_privileges').some(
    row => idOf(row.user) === userId && String(findRow('mt_roles', idOf(row.role))?.code) === ROLE.operator
  )

/* ============================================================ TAT-03 — antrean & penugasan */

export function issueMove(body: { src_keys?: string[]; operator_id?: string }) {
  requireRole('Penerbitan tugas pindah hanya oleh Admin atau Supervisor WH RM.', ROLE.admin, ROLE.supervisor)
  const wanted = new Set(body.src_keys ?? [])
  if (!wanted.size) fail('Belum ada baris yang dipilih.')
  const operator = String(body.operator_id ?? '')
  if (!isOperatorRm(operator)) fail('Pilih petugas Operator WH RM.')
  const picked = queue().rows.filter(row => wanted.has(row.key) && !row.task_no && row.suggested_code)
  if (!picked.length) fail('Belum ada baris yang dipilih.')
  const taskId = newId()
  const pdhNo = nextDocNo('PDH', 'rm_move_task', 'pdh_no')
  insertRow('rm_move_task', {
    ...auditFieldsForCreate(),
    id: taskId,
    pdh_no: pdhNo,
    source_type: 'QUEUE',
    operator_id: operator,
    by_id: currentUserId(),
    task_status: 'ASSIGNED',
    started_at: null,
    completed_at: null
  })
  picked.forEach(row =>
    insertRow('rm_move_task_item', {
      ...auditFieldsForCreate(),
      id: newId(),
      task_id: taskId,
      unit_id: row.unit_id,
      material_id: row.material_id,
      qty: row.qty,
      from_rack_bin_id: binByCode(row.rack_bin_code)?.id ?? null,
      suggested_rack_bin_id: binByCode(String(row.suggested_code))?.id ?? null,
      final_rack_bin_id: null,
      item_status: 'PENDING',
      override_reason: null,
      done_at: null
    })
  )
  txLog(
    'Penataan Gudang',
    'TAT-03',
    'tat-terbit',
    pdhNo,
    `Tugas pindah ${pdhNo} diterbitkan — ${picked.length} unit berlabel untuk ${userName(operator)}`
  )
  notify({
    to: { userIds: [operator] },
    type: 'MOVE_TASK_ASSIGNED',
    title: `Tugas pindah material ${pdhNo}`,
    body: `${picked.length} unit berlabel perlu dipindahkan ke rak tujuan — buka Pindah Material — Petugas.`,
    link: wmsRoute('warehouse-setup-tat/pindah-material-petugas'),
    ref: { type: 'rm_move_task', id: taskId }
  })
  return { pdh_no: pdhNo, items: picked.length }
}

/* ============================================================ TAT-04 — petugas pindah */

const taskOf = (id: unknown) => {
  const row = findRow('rm_move_task', String(id ?? ''))
  if (!row) fail('Tugas pindah tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row
}
const itemOf = (id: unknown) => {
  const row = findRow('rm_move_task_item', String(id ?? ''))
  if (!row) fail('Baris tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row
}
const requireMover = () => requireRole('Pemindahan hanya bisa dilakukan operator gudang RM.', ROLE.operator)
const requireOwnTask = (task: LocalRow) => {
  if (idOf(task.operator_id) !== currentUserId())
    fail('Tugas ini ditugaskan ke petugas lain.', 'ERR_UNAUTHORIZED_ROLE', 403)
  if (['COMPLETED', 'CANCELLED'].includes(String(task.task_status)))
    fail('Tugas ini sudah selesai.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
}

export function moveGate(body: { task_id?: string; from_rack_bin_id?: string; code?: string }) {
  requireMover()
  const task = taskOf(body.task_id)
  requireOwnTask(task)
  const bin = binOf(body.from_rack_bin_id)
  if (!bin) fail('Rak asal tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (up(body.code) === up(bin.code)) {
    if (task.task_status === 'ASSIGNED')
      updateRow('rm_move_task', String(task.id), {
        task_status: 'IN_PROGRESS',
        started_at: iso(),
        ...auditFieldsForUpdate()
      })
    return { rack_bin_code: bin.code }
  }
  if (codeKind(String(body.code ?? '')) === 'BIN')
    fail(
      `Kode ${String(body.code)} adalah rak lain. Daftar barang dibuka dengan scan rak asal ${String(bin.code)}.`,
      'ERR_WRONG_RACK'
    )
  fail('Langkah ini membuka isi rak asal. Scan barcode rak tempat barangnya berdiri sekarang.', 'ERR_WRONG_CODE')
}

/**
 * Scan label barang di rak asal yang sudah dibuka (v7.50: setiap kemasan berlabel). Dengan `item_id` label harus milik
 * baris itu; dengan `task_id` + `from_rack_bin_id` label menentukan barisnya sendiri (tanpa tombol per baris).
 */
export function moveScanUnit(body: { item_id?: string; task_id?: string; from_rack_bin_id?: string; code?: string }) {
  requireMover()
  const code = up(body.code)
  const item = body.item_id
    ? itemOf(body.item_id)
    : readTable('rm_move_task_item').find(
        row =>
          idOf(row.task_id) === String(body.task_id ?? '') &&
          idOf(row.from_rack_bin_id) === String(body.from_rack_bin_id ?? '') &&
          row.item_status === 'PENDING' &&
          up(findRow('rm_unit', idOf(row.unit_id))?.qr_code) === code
      )
  const task = item ? taskOf(item.task_id) : taskOf(body.task_id)
  requireOwnTask(task)
  const unit = item ? findRow('rm_unit', idOf(item.unit_id)) : undefined
  if (item && unit && code === up(unit.qr_code)) {
    if (item.item_status !== 'PENDING')
      fail('Baris ini sudah berubah — muat ulang layarnya.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
    return { item_id: String(item.id), qr_code: unit.qr_code }
  }
  const kind = codeKind(code)
  const expected = unit ? ` Scan label ${String(unit.qr_code)}.` : ' Scan label barang yang ada di daftar rak ini.'
  if (kind === 'BIN') fail(`Rak asal sudah terbuka. Sekarang scan label barangnya.${expected}`, 'ERR_WRONG_CODE')
  if (kind === 'UNIT')
    fail(`Label ${String(body.code)} bukan barang yang perlu dipindah dari rak ini.${expected}`, 'ERR_WRONG_UNIT')
  fail(`Kode ini bukan label unit Raw Material.${expected}`, 'ERR_WRONG_CODE')
}

type CommitResult = { unit_id: string; rack_bin_code: string; override: boolean }

function placeUnit(unit: LocalRow, code: string, reason: string, docRef: string): CommitResult {
  const kind = codeKind(code)
  const bin = kind === 'BIN' ? binByCode(code) : undefined
  if (!bin) {
    if (kind === 'ZONE') fail(`${up(code)} adalah area khusus, bukan rak penyimpanan biasa.`, 'ERR_WRONG_CODE')
    fail('Langkah ini meminta barcode rak tujuan, bukan label barang.', 'ERR_WRONG_CODE')
  }
  if (idOf(unit.rack_bin_id) === bin.id)
    fail(`Barangnya sudah berdiri di ${String(bin.code)}. Scan rak tujuan yang berbeda dari rak asal.`)
  const compliant = isCompliant(bin.id, unit.material_id)
  if (!compliant && !reason.trim())
    fail('Alasannya wajib ditulis — kalimat inilah yang dibaca orang berikutnya.', 'ERR_REASON_REQUIRED')
  const from = unit.rack_bin_id
  updateRow('rm_unit', String(unit.id), {
    rack_bin_id: bin.id,
    warehouse_zone_id: rackOfBin(bin)?.zone_id ?? null,
    place_note: null,
    override_reason: compliant ? null : reason.trim(),
    ...auditFieldsForUpdate()
  })
  stockMovement('RELOCATE', unit, num(unit.qty_small), from, bin.id, docRef)
  return { unit_id: String(unit.id), rack_bin_code: String(bin.code), override: !compliant }
}

export function moveCommit(body: { item_id?: string; code?: string; reason?: string }) {
  requireMover()
  const item = itemOf(body.item_id)
  const task = taskOf(item.task_id)
  requireOwnTask(task)
  if (item.item_status !== 'PENDING')
    fail('Baris ini sudah berubah — muat ulang layarnya.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const unit = findRow('rm_unit', idOf(item.unit_id))
  if (!unit) fail('Unit barang ini tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const result = placeUnit(unit, String(body.code ?? ''), String(body.reason ?? ''), String(task.pdh_no))
  updateRow('rm_move_task_item', String(item.id), {
    item_status: 'DONE',
    final_rack_bin_id: binByCode(result.rack_bin_code)?.id ?? null,
    override_reason: result.override ? String(body.reason ?? '').trim() : null,
    done_at: iso(),
    ...auditFieldsForUpdate()
  })
  const left = readTable('rm_move_task_item').filter(
    row => idOf(row.task_id) === task.id && row.item_status === 'PENDING'
  )
  if (!left.length)
    updateRow('rm_move_task', String(task.id), {
      task_status: 'COMPLETED',
      completed_at: iso(),
      ...auditFieldsForUpdate()
    })
  txLog(
    'Penataan Gudang',
    'TAT-04',
    'tat-pindah',
    String(task.pdh_no),
    `${String(materialOf(unit.material_id)?.code)} ${String(unit.qr_code)} — ${String(binOf(item.from_rack_bin_id)?.code ?? '—')} → ${result.rack_bin_code}${result.override ? ` [ALASAN: ${String(body.reason).trim()}]` : ' (sesuai usulan)'}`
  )
  return { ...result, task_done: !left.length }
}

export function selfContents(body: { code?: string }) {
  requireMover()
  const bin = binByCode(String(body.code ?? ''))
  if (!bin) {
    if (codeKind(String(body.code ?? '')) === 'UNKNOWN' || codeKind(String(body.code ?? '')) === 'ZONE')
      fail('Langkah ini membuka isi rak asal. Scan barcode rak tempat barangnya berdiri sekarang.', 'ERR_WRONG_CODE')
    fail('Scan kode rak, bukan barang. Scan barcode rak tempat barangnya berdiri sekarang.', 'ERR_WRONG_CODE')
  }
  return {
    bin: { id: bin.id, code: bin.code, rack_name: rackOfBin(bin)?.name ?? '' },
    units: unitsInStock()
      .filter(unit => idOf(unit.rack_bin_id) === bin.id)
      .map(unit => {
        const material = materialOf(unit.material_id) as LocalRow
        return {
          kind: 'UNIT',
          id: unit.id,
          qr_code: unit.qr_code,
          qty: Math.round(unitPacks(unit) * 100) / 100,
          compliant: isCompliant(bin.id, unit.material_id),
          material_id: String(material.id),
          material_code: String(material.code),
          material_name: String(material.name),
          category_id: idOf(material.category_id) || null,
          unit_name: String(unit.unit_big_snapshot ?? '') || bigUnitNameOf(material)
        }
      })
  }
}

export function selfCommit(body: { unit_id?: string; code?: string; reason?: string }) {
  requireMover()
  const unit = findRow('rm_unit', String(body.unit_id ?? ''))
  if (!unit || unit.unit_status !== 'IN_STOCK')
    fail('Baris ini sudah berubah — muat ulang layarnya.', 'ERR_NOT_FOUND', 404)
  const pdhNo = nextDocNo('PDH', 'rm_move_task', 'pdh_no')
  const from = unit.rack_bin_id
  const result = placeUnit(unit, String(body.code ?? ''), String(body.reason ?? ''), pdhNo)
  const taskId = newId()
  insertRow('rm_move_task', {
    ...auditFieldsForCreate(),
    id: taskId,
    pdh_no: pdhNo,
    source_type: 'SELF',
    operator_id: currentUserId(),
    by_id: currentUserId(),
    task_status: 'COMPLETED',
    started_at: iso(),
    completed_at: iso()
  })
  insertRow('rm_move_task_item', {
    ...auditFieldsForCreate(),
    id: newId(),
    task_id: taskId,
    unit_id: unit.id,
    material_id: unit.material_id,
    qty: Math.round(unitPacks(unit) * 100) / 100,
    from_rack_bin_id: from ?? null,
    suggested_rack_bin_id: suggestBin(idOf(unit.material_id))?.id ?? null,
    final_rack_bin_id: binByCode(result.rack_bin_code)?.id ?? null,
    item_status: 'DONE',
    override_reason: result.override ? String(body.reason ?? '').trim() : null,
    done_at: iso()
  })
  txLog(
    'Penataan Gudang',
    'TAT-04',
    'tat-pindah',
    pdhNo,
    `Pindah mandiri ${String(materialOf(unit.material_id)?.code)} ${String(unit.qr_code)} → ${result.rack_bin_code}${result.override ? ` [ALASAN: ${String(body.reason).trim()}]` : ''}`
  )
  return { ...result, pdh_no: pdhNo }
}

export function suggestBinFor(body: { material_id?: string }) {
  const bin = suggestBin(String(body.material_id ?? ''), reservedBins())
  return {
    rack_bin_code: bin ? String(bin.code) : null,
    note: bin ? suggestionNote(bin, String(body.material_id)) : null
  }
}

/* ============================================================ registrasi */

export function registerWarehouseSetupHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/setup-tat'
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  post('issue-move', issueMove)
  post('move-gate', moveGate)
  post('move-scan-unit', moveScanUnit)
  post('move-commit', moveCommit)
  post('self-contents', selfContents)
  post('self-commit', selfCommit)
  post('suggest-bin', suggestBinFor)
  mock.onGet(`${base}/progress`).reply(() => handle(() => progress()))
  mock.onGet(`${base}/queue`).reply(() => handle(() => queue()))
}
